import type { Middleware } from 'redux'
import type { Logger, LoggerTrackProperties, LoggerUser } from './types'
import type { LoggerProvider } from './providers/types'

function getErrorMessage(error: object): string {
  const err = error as { payload?: { message?: string }; message?: string }

  return err.payload?.message || err.message || JSON.stringify(error)
}

export class AppLogger implements Logger {
  private providers: LoggerProvider[]
  private initialized = false

  constructor(providers: LoggerProvider[]) {
    this.providers = providers
  }

  init(): void {
    if (this.initialized) {
      return
    }

    this.providers.forEach((p) => p.init?.())
    this.initialized = true
  }

  isInitialized(): boolean {
    return this.initialized
  }

  identify(user: LoggerUser): void {
    this.providers.forEach((p) => p.identify?.(user))
  }

  error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
    console.error(message, error, data)

    let errorObj: Error

    if (error instanceof Error) {
      errorObj = error
    } else if (error && typeof error === 'object') {
      errorObj = new Error(getErrorMessage(error))
    } else if (error) {
      errorObj = new Error(String(error))
    } else {
      errorObj = new Error(message)
    }

    this.providers.forEach((p) => p.error(message, errorObj, data))
  }

  warn(message: string, data?: Record<string, unknown>): void {
    console.warn(message, data)
    this.providers.forEach((p) => p.warn(message, data))
  }

  info(message: string, data?: Record<string, unknown>): void {
    console.info(message, data)
    this.providers.forEach((p) => p.info(message, data))
  }

  track(event: string, properties?: LoggerTrackProperties): void {
    this.providers.forEach((provider) => {
      provider.track?.(event, properties)
    })
  }

  getSessionURL(): string | null {
    return this.providers.find((p) => p.getSessionURL)?.getSessionURL?.() ?? null
  }

  onSessionURLReady(callback: (url: string) => void): void {
    const provider = this.providers.find((p) => p.onSessionURLReady)
    provider?.onSessionURLReady?.(callback)
  }

  createReduxMiddleware(): Middleware {
    const provider = this.providers.find((p) => p.createReduxMiddleware)

    return provider?.createReduxMiddleware?.() ?? (() => (next) => (action) => next(action))
  }
}
